import {useEffect, useState} from "react";
import * as React from "react";
import {
  Table,
  TableContainer,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  chakra,
  IconButton,
  Flex,
  Spacer,
  Box,
  Select, Input
} from "@chakra-ui/react";

import {
  ArrowBackIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  TriangleDownIcon,
  TriangleUpIcon
} from "@chakra-ui/icons";

import {
  useReactTable,
  flexRender,
  getCoreRowModel,
  ColumnDef,
  getSortedRowModel,
  getPaginationRowModel,
  getFilteredRowModel,
  FilterFns,
  FilterFn
} from "@tanstack/react-table";

import {rankItem, compareItems} from '@tanstack/match-sorter-utils'


export function DataTable(props) {
  const {data, columns, defaultSorting} = props
  const [sorting, setSorting] = useState(defaultSorting || []);
  const [globalFilter, setGlobalFilter] = useState('')
  const [rowPinning, setRowPinning] = useState({
    top: [],
    bottom: [],
  })

  const fuzzyFilter = (row, columnId, value, addMeta) => {
    // Rank the item
    const itemRank = rankItem(row.getValue(columnId), value)

    // Store the ranking info
    addMeta(itemRank)

    // Return if the item should be filtered in/out
    return itemRank.passed
  }

  const table = useReactTable({
    columns,
    data,
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onGlobalFilterChange: setGlobalFilter,
    onRowPinningChange: setRowPinning,
    keepPinnedRows: true,
    enablePinning: false,
    globalFilterFn: fuzzyFilter,
    state: {
      sorting,
      globalFilter,
      rowPinning,
    }
  });

  return (
    <div>
      <Input
        variant={'flushed'}
        value={globalFilter ?? ''}
        onChange={e => setGlobalFilter(String(e.target.value))}
        marginBottom={5}
        placeholder="Buscar"
      />
      <Table variant='striped'  {...props}>
        <Thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <Tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                // see https://tanstack.com/table/v8/docs/api/core/column-def#meta to type this correctly
                const meta = header.column.columnDef.meta;
                return (
                  <Th
                    key={header.id}
                    onClick={header.column.getToggleSortingHandler()}
                    isNumeric={meta?.isNumeric}
                  >
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}

                    <chakra.span pl="4">
                      {header.column.getIsSorted() ? (
                        header.column.getIsSorted() === "desc" ? (
                          <TriangleDownIcon aria-label="sorted descending"/>
                        ) : (
                          <TriangleUpIcon aria-label="sorted ascending"/>
                        )
                      ) : null}
                    </chakra.span>
                  </Th>
                );
              })}
            </Tr>
          ))}
        </Thead>
        <Tbody>
          {table.getRowModel().rows.map((row) => (
            <Tr key={row.id}>
              {row.getVisibleCells().map((cell) => {
                // see https://tanstack.com/table/v8/docs/api/core/column-def#meta to type this correctly
                const meta = cell.column.columnDef.meta;
                return (
                  <Td key={cell.id} isNumeric={meta?.isNumeric}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </Td>
                );
              })}
            </Tr>
          ))}
        </Tbody>
      </Table>
      <Flex marginTop={4}>
        <Box>
          Pagina {' '}
          <strong>
            {table.getState().pagination.pageIndex + 1} de{' '}
            {table.getPageCount()}
          </strong>
        </Box>
        <Spacer/>
        <Box>

          <IconButton
            size={'sm'}
            isRound={true}
            variant='outline'
            aria-label='Done'
            onClick={() => table.setPageIndex(0)}
            disabled={!table.getCanPreviousPage()}
            icon={<ArrowLeftIcon/>}
          />
          <IconButton
            marginX={'2'}
            size={'sm'}
            isRound={true}
            variant='outline'
            aria-label='Done'
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
            icon={<ChevronLeftIcon/>}
          />

          <IconButton
            size={'sm'}
            marginX={'2'}
            isRound={true}
            variant='outline'
            aria-label='Done'
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
            icon={<ChevronRightIcon/>}
          />
          <IconButton
            size={'sm'}
            isRound={true}
            variant='outline'
            aria-label='Done'
            onClick={() => table.setPageIndex(table.getPageCount() - 1)}
            disabled={!table.getCanNextPage()}
            icon={<ArrowRightIcon/>}
          />
        </Box>
        <Spacer/>

        <Box>
          <Select
            size={'sm'}
            value={table.getState().pagination.pageSize}
            onChange={e => {
              table.setPageSize(Number(e.target.value))
            }}
          >
            {[10, 20, 30, 40, 50, 100].map(pageSize => (
              <option key={pageSize} value={pageSize}>
                Mostrar {pageSize}
              </option>
            ))}
          </Select>
        </Box>
      </Flex>
    </div>
  );
}